import { useTranslation } from "react-i18next";
import { HTMLAttributes } from "react";

import { Button, ButtonVariant } from "@industriousoffice/react-components";
import { useNavigate } from "react-router-dom";
import { Account, Location } from "@/api/opb/opbTypes";
import { getFullPath } from "@/router/utils/getFullPath";
import { slugs } from "@/router/slugs";
import useUser from "@/hooks/useUser";
import { useDateFormatter } from "@/hooks/useDateFormatter";

interface SecurityDepositBannerProps extends HTMLAttributes<HTMLDivElement> {
  account: Account;
  location?: Location;
}

export function SecurityDepositBanner({
  account,
  location,
  className = "",
  ...props
}: SecurityDepositBannerProps) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { format } = useDateFormatter();

  const { securityDeposit } = account;

  const { user: lastSubmittedBy, isLoading: isLoadingUser } = useUser(
    securityDeposit?.lastSubmittedBy
  );

  if (isLoadingUser && securityDeposit?.lastSubmittedBy) {
    return null;
  }

  const navigateToSecurityDepositForm = () => {
    navigate(getFullPath(slugs.securityDeposit, { accountId: account._id }));
  };

  const name = location
    ? `${location.city} — ${location.name}`
    : account.globalAccountName;

  const moveOutDate = format(account.moveOutDate, "PP", "UTC"); // Always format in UTC because date is orginally created from YYYY-MM-DD with UtC

  return (
    <div
      className={`rounded-lg py-3 px-5 ${
        securityDeposit ? "bg-ocean-99" : "bg-honey-99"
      } text-sm shadow-card ${className}`}
      {...props}
    >
      <div className="font-bold" data-testid="security_deposit_banner-title">
        {name}
      </div>
      {securityDeposit?.lastSubmittedAt && lastSubmittedBy ? (
        <div className="flex flex-col mt-2 sm:flex-row">
          <div className="font-bold">{t("Security Deposit Form Received")}</div>
          <div className="hidden sm:mx-2 sm:block">—</div>
          <div className="mt-2 sm:mt-0">
            {t("Submitted by", {
              name: `${lastSubmittedBy?.firstName || ""} ${
                lastSubmittedBy?.lastName || ""
              }`,
              date: format(
                securityDeposit.lastSubmittedAt,
                "PP",
                account.timezone
              ),
            })}
          </div>
        </div>
      ) : null}
      <div className="flex flex-col items-center mt-2 sm:flex-row">
        <div className="mr-4">
          {t(
            securityDeposit?.bankDocumentSubmitted
              ? "If you would like to change or update your bank account details, you can resubmit the form before your move-out date at this location on date. This will overwrite any previous submissions and the refund will be issued according to the most recent form submission."
              : "You have requested a move-out. Please submit your security deposit refund form before your move-out date at this location on",
            { date: moveOutDate }
          )}
        </div>
        <Button
          variant={ButtonVariant.Outline}
          onClick={navigateToSecurityDepositForm}
          data-testid="security_deposit_banner-btn"
          className="whitespace-nowrap bg-transparent! border-2 border-grey-30! text-black! mt-4 sm:mt-0 sm:w-fit w-full"
        >
          {t("Go to Security Deposit Form")}
        </Button>
      </div>
    </div>
  );
}
